import * as React from "react";
import {
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AutocompleteInput,
  BooleanInput,
  RadioButtonGroupInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  email,
  minLength,
  required,
  useCreate,
  useGetIdentity,
  useNotify,
} from "react-admin";
import { useFormContext } from "react-hook-form";

import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { Buyer } from "../types";
import { PersonReferenceShortcuts } from "../suppliers/PersonErpLink";
import { Avatar } from "./Avatar";

export const ContactInputs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack gap={2} p={1}>
      <Avatar />
      <Stack gap={4} direction={isMobile ? "column" : "row"}>
        <Stack gap={4} flex={1}>
          <ContactIdentityInputs />
          <ContactPositionInputs />
        </Stack>
        <Divider orientation={isMobile ? "horizontal" : "vertical"} flexItem />
        <Stack gap={4} flex={1}>
          <ContactPersonalInformationInputs />
          <ContactMiscInputs />
        </Stack>
      </Stack>
    </Stack>
  );
};

const ContactIdentityInputs = () => {
  const { contactGender } = useConfigurationContext();
  return (
    <Stack>
      <Typography variant="h6">Identificação</Typography>
      <RadioButtonGroupInput
        label={false}
        source="sexo"
        choices={contactGender}
        helperText={false}
        optionText="label"
        optionValue="value"
        sx={{ "& .MuiRadio-root": { paddingY: 0 } }}
        defaultValue={contactGender[0].value}
      />
      <TextInput
        source="nome_contato"
        validate={required()}
        helperText={false}
      />
      <TextInput source="cpf" label="CPF" helperText={false} />
    </Stack>
  );
};

const ContactPositionInputs = () => {
  const [create] = useCreate();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const handleCreateCompany = async (name?: string) => {
    if (!name) return;
    try {
      const newCompany = await create(
        "suppliers",
        {
          data: {
            nome_razao: name,
            cod_vendedor: identity?.id,
          },
        },
        { returnPromise: true },
      );
      return newCompany;
    } catch (error) {
      notify("Erro criando fornecedor", {
        type: "error",
      });
    }
  };
  return (
    <Stack>
      <Typography variant="h6">Cargo/Relacionamento</Typography>
      <ReferenceInput
        source="tipo"
        reference="contacttypes"
        sort={{ field: "descricao_tipo", order: "ASC" }}
      >
        <SelectInput
          label="Tipo de Contato"
          optionText={(choice) => choice.descricao_tipo}
          helperText={false}
        />
      </ReferenceInput>
      <ReferenceInput source="id_pessoa" reference="persons">
        <AutocompleteInput
          optionText="nome_razao"
          filterToQuery={(pesquisa) => ({ pesquisa })}
          onCreate={handleCreateCompany}
          helperText={false}
        />
      </ReferenceInput>
      <PersonReferenceShortcuts source="id_pessoa" />
    </Stack>
  );
};

const ContactPersonalInformationInputs = () => {
  const { getValues, setValue } = useFormContext();

  // set first and last name based on email
  const handleEmailChange = (email: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !email) return;
    const [first, last] = email.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue("last_name", last.charAt(0).toUpperCase() + last.slice(1));
  };

  const handleEmailPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <Stack>
      <Typography variant="h6">Dados pessoais</Typography>
      <TextInput
        source="email"
        helperText={false}
        validate={email()}
        onPaste={handleEmailPaste}
        onBlur={handleEmailBlur}
      />
      <Stack gap={1} flexDirection="row">
        <TextInput
          source="telefone"
          helperText={false}
          validate={minLength(8)}
        />
        <ReferenceInput
          source="type_id"
          reference="phonetypes"
          sort={{ field: "tipo", order: "ASC" }}
        >
          <SelectInput
            label="Tipo de Telefone"
            optionText={(choice) => choice.tipo}
            helperText={false}
          />
        </ReferenceInput>
      </Stack>
    </Stack>
  );
};

const ContactMiscInputs = () => {
  return (
    <Stack>
      <Typography variant="h6">Informações adicionais</Typography>
      <TextInput
        source="observacoes"
        label="Observações"
        multiline
        helperText={false}
      />
      <BooleanInput
        source="relacionamento.autorizado"
        label="Autorizado a comprar"
        helperText={false}
      />
      <BooleanInput source="ativo" label="Ativo" helperText={false} />
    </Stack>
  );
};

const saleOptionRenderer = (choice: Buyer) =>
  `${choice.nome_razao} ${choice.caminho_canal}`;
